import {
  type Auth0Client,
  createAuth0Client as createSpaClient,
} from "@auth0/auth0-spa-js";
import { isWindowDefined } from "@huub-web/utils";
import auth0, {
  type Auth0DecodedHash,
  type Auth0Error,
  type AuthOptions,
} from "auth0-js";

import { Auth0ServiceError } from "../types";

// Re-export Auth0Client type for use in components
export type { Auth0Client } from "@auth0/auth0-spa-js";

/**
 * Auth0 authentication result containing tokens.
 * Uses Auth0DecodedHash with required token fields.
 */
export type AuthResult = Required<
  Pick<
    Auth0DecodedHash,
    "accessToken" | "idToken" | "expiresIn" | "refreshToken"
  >
>;

/**
 * Auth0 WebAuth configuration options
 */
type WebAuthConfig = {
  domain: string;
  clientId: string;
  redirectUri: string;
  audience?: string;
};

/**
 * Auth0 SPA SDK configuration options
 */
type SpaAuthConfig = {
  domain: string;
  clientId: string;
  redirectUri: string;
  audience?: string;
  /**
   * Locale for Auth0 Universal Login UI (default: "nl")
   */
  locale?: string;
};

/**
 * Default OAuth scope for Auth0 authentication.
 * Includes offline_access for refresh token support.
 */
const AUTH0_SCOPE = "openid profile email offline_access";

/**
 * Map of Auth0 snake_case error codes to camelCase i18n key suffixes.
 * Used with `authErrors.*` translation keys.
 */
const ERROR_CODE_MAP: Record<string, string> = {
  invalid_grant: "invalidGrant",
  invalid_password: "invalidPassword",
  invalid_user_password: "invalidUserPassword",
  wrong_email_or_password: "wrongEmailOrPassword",
  unauthorized: "unauthorized",
  access_denied: "accessDenied",
  blocked: "blocked",
  blocked_user: "blockedUser",
  too_many_attempts: "tooManyAttempts",
  too_many_requests: "tooManyRequests",
  user_exists: "userExists",
  invalid_signup: "invalidSignup",
  password_strength_error: "passwordStrengthError",
  password_no_user_info_error: "passwordNoUserInfoError",
  password_dictionary_error: "passwordDictionaryError",
  password_leaked: "passwordLeaked",
  email_not_verified: "emailNotVerified",
  mfa_required: "mfaRequired",
  mfa_registration_required: "mfaRegistrationRequired",
  invalid_otp: "invalidOtp",
  network_error: "networkError",
  timeout: "timeout",
  server_error: "serverError",
};

/**
 * Get i18n error code from Auth0 error.
 * Returns a camelCase key suffix for use with `authErrors.*` translations.
 */
const getErrorCode = (error: Auth0Error): string => {
  const code = error.code || error.error || "";
  const name = error.name || "";

  // Check for password strength error first (has code "invalid_password" but name "PasswordStrengthError")
  if (name === "PasswordStrengthError" || code === "password_strength_error") {
    return "passwordStrengthError";
  }

  // Check for specific error codes
  if (ERROR_CODE_MAP[code]) {
    return ERROR_CODE_MAP[code];
  }

  // Get description as string - Auth0 sometimes returns an object for password errors
  const rawDescription = error.description || error.error_description || "";
  const description =
    typeof rawDescription === "string"
      ? rawDescription.toLowerCase()
      : JSON.stringify(rawDescription).toLowerCase();

  // Pattern matching for common English error descriptions
  if (
    description.includes("wrong email or password") ||
    description.includes("wrong username or password") ||
    description.includes("invalid credentials")
  ) {
    return "invalidGrant";
  }

  if (description.includes("password") && description.includes("strength")) {
    return "passwordStrengthError";
  }

  if (description.includes("password") && description.includes("leak")) {
    return "passwordLeaked";
  }

  if (description.includes("user") && description.includes("exists")) {
    return "userExists";
  }

  if (description.includes("user") && description.includes("block")) {
    return "blocked";
  }

  if (description.includes("too many")) {
    return "tooManyAttempts";
  }

  if (description.includes("email") && description.includes("verif")) {
    return "emailNotVerified";
  }

  if (description.includes("network") || description.includes("connection")) {
    return "networkError";
  }

  return "default";
};

/**
 * Creates and returns an Auth0 WebAuth client
 */
export const createWebAuth = (config: WebAuthConfig): auth0.WebAuth => {
  const options: AuthOptions = {
    domain: config.domain,
    clientID: config.clientId,
    redirectUri: config.redirectUri,
    responseType: "token id_token",
    scope: AUTH0_SCOPE,
  };

  if (config.audience) {
    options.audience = config.audience;
  }

  return new auth0.WebAuth(options);
};

/**
 * Creates and returns an Auth0 SPA SDK client for social login.
 * Uses authorization code flow with PKCE for better security and refresh token support.
 * @param config - SPA SDK configuration options
 * @returns Promise resolving to Auth0Client instance
 */
export const createAuth0Client = (
  config: SpaAuthConfig,
): Promise<Auth0Client> =>
  createSpaClient({
    domain: config.domain,
    clientId: config.clientId,
    authorizationParams: {
      redirect_uri: config.redirectUri,
      scope: AUTH0_SCOPE,
      audience: config.audience,
      ui_locales: config.locale ?? "nl",
    },
    useRefreshTokens: true,
    cacheLocation: "localstorage",
  });

/**
 * Login with email and password using Auth0's Resource Owner Password Grant
 * @param webAuth - Auth0 WebAuth instance
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving to AuthResult with tokens
 * @throws Auth0ServiceError with i18n error code
 */
export const loginWithPassword = (
  webAuth: auth0.WebAuth,
  email: string,
  password: string,
): Promise<AuthResult> =>
  new Promise((resolve, reject) => {
    webAuth.client.login(
      {
        realm: "Username-Password-Authentication",
        username: email,
        password: password,
        scope: AUTH0_SCOPE,
      },
      (err, result: Auth0DecodedHash | null) => {
        if (err) {
          reject(new Auth0ServiceError(getErrorCode(err)));
          return;
        }

        if (!result?.accessToken || !result?.idToken || !result?.expiresIn) {
          reject(new Auth0ServiceError("default"));
          return;
        }

        resolve({
          accessToken: result.accessToken,
          idToken: result.idToken,
          refreshToken: result.refreshToken ?? "",
          expiresIn: result.expiresIn,
        });
      },
    );
  });

/**
 * Sign up a new user with email and password
 * @param webAuth - Auth0 WebAuth instance
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving when signup is complete
 * @throws Auth0ServiceError with i18n error code
 */
export const signupUser = (
  webAuth: auth0.WebAuth,
  email: string,
  password: string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    webAuth.signup(
      {
        connection: "Username-Password-Authentication",
        email: email,
        password: password,
      },
      (err) => {
        if (err) {
          reject(new Auth0ServiceError(getErrorCode(err)));
          return;
        }

        resolve();
      },
    );
  });

/**
 * Sign up a new user and automatically log them in
 * @param webAuth - Auth0 WebAuth instance
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving to AuthResult with tokens
 * @throws Auth0ServiceError with i18n error code
 */
export const signupWithPassword = async (
  webAuth: auth0.WebAuth,
  email: string,
  password: string,
): Promise<AuthResult> => {
  // Step 1: Create the user account
  await signupUser(webAuth, email, password);

  // Step 2: Automatically log in the new user
  return loginWithPassword(webAuth, email, password);
};

/**
 * Login with a social provider (Google, Microsoft, etc.) using SPA SDK.
 * This redirects the user to the provider's login page using authorization code flow with PKCE.
 * @param auth0Client - Auth0 SPA SDK client instance
 * @param provider - The Auth0 connection name (e.g., 'google-oauth2', 'windowslive')
 * @param locale - Locale for Auth0 UI (default: "nl")
 */
export const loginWithProvider = async (
  auth0Client: Auth0Client,
  provider: string,
  locale = "nl",
): Promise<void> => {
  await auth0Client.loginWithRedirect({
    authorizationParams: {
      connection: provider,
      scope: AUTH0_SCOPE,
      ui_locales: locale,
    },
  });
};

/**
 * Parse the URL hash after a redirect callback and extract tokens.
 * Used as fallback for hash-based callbacks.
 * @param webAuth - Auth0 WebAuth instance
 * @returns Promise resolving to AuthResult with tokens
 * @throws Auth0ServiceError with i18n error code
 */
export const parseHashCallback = (
  webAuth: auth0.WebAuth,
): Promise<AuthResult> =>
  new Promise((resolve, reject) => {
    webAuth.parseHash((err, result: Auth0DecodedHash | null) => {
      if (err) {
        reject(new Auth0ServiceError(getErrorCode(err)));
        return;
      }

      if (!result?.accessToken || !result?.idToken || !result?.expiresIn) {
        reject(new Auth0ServiceError("default"));
        return;
      }

      resolve({
        accessToken: result.accessToken,
        idToken: result.idToken,
        refreshToken: result.refreshToken ?? "",
        expiresIn: result.expiresIn,
      });
    });
  });

/**
 * Extract refresh token from Auth0 SPA SDK's localStorage cache.
 * Note: This accesses internal SDK storage which is not part of the public API.
 * @param clientId - The Auth0 client ID
 * @returns The refresh token if found, otherwise null
 */
const getRefreshTokenFromCache = (clientId: string): string | null => {
  if (!isWindowDefined() || !clientId) {
    return null;
  }

  const keyPrefix = `@@auth0spajs@@::${clientId}::`;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(keyPrefix)) {
      const cached = localStorage.getItem(key);
      if (cached) {
        try {
          const data = JSON.parse(cached) as {
            body?: { refresh_token?: string };
          };
          if (data.body?.refresh_token) {
            return data.body.refresh_token;
          }
        } catch {
          // Ignore JSON parse errors
        }
      }
    }
  }
  return null;
};

/**
 * Handle the social login callback using SPA SDK.
 * Exchanges the authorization code for tokens including refresh token.
 * @param auth0Client - Auth0 SPA SDK client instance
 * @param clientId - The Auth0 client ID for extracting refresh token from cache
 * @returns Promise resolving to AuthResult with tokens
 * @throws Auth0ServiceError with i18n error code
 */
export const handleSocialCallback = async (
  auth0Client: Auth0Client,
  clientId: string,
): Promise<AuthResult> => {
  // Handle the redirect callback and exchange code for tokens
  await auth0Client.handleRedirectCallback();

  // Get the access token with claims
  const accessToken = await auth0Client.getTokenSilently();
  const idTokenClaims = await auth0Client.getIdTokenClaims();

  if (!accessToken || !idTokenClaims) {
    throw new Auth0ServiceError("default");
  }

  const idToken = idTokenClaims.__raw;

  // Extract refresh token from SPA SDK's localStorage cache
  const refreshToken = getRefreshTokenFromCache(clientId) ?? "";

  return {
    accessToken,
    idToken,
    refreshToken,
    expiresIn: idTokenClaims.exp
      ? idTokenClaims.exp - Math.floor(Date.now() / 1000)
      : 3600,
  };
};

/**
 * Prefix used by Auth0 SPA SDK for localStorage keys.
 */
const AUTH0_STORAGE_PREFIX = "@@auth0spajs@@";

/**
 * Clears Auth0 SPA SDK localStorage cache.
 * Call this after successful login to clean up Auth0 tokens.
 * The tokens are no longer needed since we've transferred the id_token via cookie.
 */
export const clearAuth0Storage = (): void => {
  if (!isWindowDefined()) {
    return;
  }

  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(AUTH0_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
};
